import React from 'react';
import {
  Activity,
  Cast,
  Check,
  Layers,
  Lightbulb,
  MonitorStop,
  Radio,
  Save,
  Sliders,
  Wifi,
  WifiOff,
} from 'lucide-react';
import type { AlgorithmId, WizBulb } from '../../types/ambilight';
import { ALGORITHM_INFO_MAP } from '../../services/colorEngine';
import { useAmbilightContext } from '../../context/AmbilightContext';

export interface HeaderProps {
  primaryBulb?: WizBulb;
  fps?: number;
  isCapturing?: boolean;
  onToggleShare?: () => void;
  showZones?: boolean;
  onToggleZones?: () => void;
  isRaw?: boolean;
  onToggleRaw?: () => void;
  currentAlgorithm?: AlgorithmId;
  onSelectAlgorithm?: (algo: AlgorithmId) => void;
  onOpenLightManager?: () => void;
  onOpenTune?: () => void;
}

export const Header: React.FC<HeaderProps> = (props) => {
  const context = useAmbilightContext();

  const primaryBulb = props.primaryBulb ?? context.primaryBulb;
  const fps = props.fps ?? context.fps;
  const isCapturing = props.isCapturing ?? context.isCapturing;
  const onToggleShare = props.onToggleShare ?? context.toggleShareScreen;
  const showZones = props.showZones ?? context.showZones;
  const onToggleZones = props.onToggleZones ?? context.toggleZones;
  const isRaw = props.isRaw ?? context.isRaw;
  const onToggleRaw = props.onToggleRaw ?? context.toggleRaw;
  const currentAlgorithm = props.currentAlgorithm ?? context.algorithm;
  const onSelectAlgorithm = props.onSelectAlgorithm ?? context.setAlgorithm;
  const onOpenLightManager = props.onOpenLightManager ?? (() => context.setIsLightManagerOpen(true));
  const onOpenTune = props.onOpenTune ?? (() => context.setIsTuneOpen(true));
  const algorithms: AlgorithmId[] = [
    'perceptual',
    'weighted',
    'average',
    'dominant',
    'median',
    'saturated',
    'dynamic',
  ];

  return (
    <header className="w-full flex items-center justify-between px-6 py-2.5 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl z-30 select-none gap-4">


      {/* 2. SELECTOR DE ALGORITMOS ARRIBA (Modo Slim) */}
      <div className="flex items-center gap-1 p-1 rounded-full bg-slate-900/90 border border-white/10 shadow-inner backdrop-blur-md overflow-x-auto">
        {algorithms.map((algoId) => {
          const info = ALGORITHM_INFO_MAP[algoId];
          const isActive = currentAlgorithm === algoId;
          return (
            <button
              key={algoId}
              onClick={() => onSelectAlgorithm?.(algoId)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${isActive
                  ? 'bg-purple-600 text-white font-semibold shadow-md shadow-purple-600/35 ring-1 ring-purple-400/50'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              title={info.desc}
            >
              <span className="text-[12px]">{info.icon}</span>
              <span className="text-[11px] font-sans tracking-wide">{info.shortName}</span>
            </button>
          );
        })}
      </div>

      {/* 3. Controles Rápidos: Modo Crudo, Foco WiZ, Zonas, Ajuste Fino y Compartir Pantalla */}
      <div className="flex items-center gap-2 text-xs shrink-0">
        {/* Toggle Directo: Modo Crudo */}
        <button
          onClick={onToggleRaw}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${isRaw
              ? 'border-amber-500/60 bg-amber-500/20 text-amber-200 shadow-sm shadow-amber-500/20'
              : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          title="Modo crudo: enviar color exacto sin procesar"
        >
          <span>🧪</span>
          <span>
            Crudo:{' '}
            <strong className={isRaw ? 'text-amber-300' : 'text-blue-400'}>
              {isRaw ? 'ON' : 'OFF'}
            </strong>
          </span>
        </button>

        {/* Foco WiZ */}
        <button
          onClick={onOpenLightManager}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 transition-colors cursor-pointer"
          title="Administrador de Focos WiZ"
        >
          <Lightbulb
            className={`w-3.5 h-3.5 ${primaryBulb.connected ? 'text-amber-400 animate-pulse' : 'text-slate-500'
              }`}
          />
          <span className="font-mono text-[11px] hidden lg:inline">
            <strong className="text-white">{primaryBulb.ip}</strong>
          </span>
          {primaryBulb.connected ? (
            <Wifi className="w-3 h-3 text-emerald-400" />
          ) : (
            <WifiOff className="w-3 h-3 text-rose-400" />
          )}
        </button>

        {/* Ver Zonas */}
        <button
          onClick={onToggleZones}
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border transition-all cursor-pointer ${showZones
              ? 'border-purple-500/60 bg-purple-500/25 text-purple-200 font-medium'
              : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          title="Mostrar u ocultar cajas de muestreo en pantalla"
        >
          <Layers className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Zonas</span>
        </button>

        {/* Ajuste Fino */}
        <button
          onClick={onOpenTune}
          className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          title="Ajuste fino de parámetros del algoritmo activo"
        >
          <Sliders className="w-3.5 h-3.5" />
        </button>

        {/* Guardar en Localhost */}
        <button
          onClick={context.saveConfigToLocalStorage}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${context.saveStatus
              ? 'border-emerald-500/70 bg-emerald-500/25 text-emerald-200 shadow-sm shadow-emerald-500/30 ring-1 ring-emerald-400/50'
              : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          title="Guardar toda la configuración actual en localhost (localStorage)"
        >
          {context.saveStatus ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-300 animate-in zoom-in-75" />
              <span className="font-semibold text-emerald-200">{context.saveStatus}</span>
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5 text-purple-300" />
              <span className="hidden md:inline">Guardar</span>
            </>
          )}
        </button>

        {/* FPS Counter */}
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg border border-white/10 bg-black/40 text-[10px] font-mono text-cyan-400">
          <Activity className="w-3 h-3 text-cyan-400" />
          <span>{fps} FPS</span>
        </div>

        {/* Botón Compartir Pantalla */}
        <button
          onClick={onToggleShare}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-medium text-xs shadow-lg transition-all active:scale-95 cursor-pointer ${isCapturing
              ? 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/30 border border-rose-400/40 animate-pulse'
              : 'bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white shadow-purple-600/40 border border-white/20'
            }`}
          title={isCapturing ? 'Detener captura de pantalla' : 'Compartir pantalla para sincronizar ambilight'}
        >
          {isCapturing ? (
            <>
              <MonitorStop className="w-3.5 h-3.5 text-white" />
              <span className="font-semibold">DETENER</span>
            </>
          ) : (
            <>
              <Cast className="w-3.5 h-3.5 text-cyan-200" />
              <span className="font-semibold tracking-wide">COMPARTIR</span>
            </>
          )}
        </button>
      </div>
    </header>
  );
};
